import { apiClient } from './apiClient';

export interface CoordinationNoteReadResponse {
  ok: boolean;
  ref: string;
  stem?: string;
  bodyMarkdown?: string;
  /** When API is not deployed yet (G3). */
  unavailable?: boolean;
  message?: string;
}

export class CoordinationNoteReadError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'CoordinationNoteReadError';
  }
}

/**
 * Read a coordination note by ref for ExoPanel hero.
 * Endpoint is the planned G3 thin-read; 404 → unavailable (UI shows path + deferred respond).
 */
export async function fetchCoordinationNote(
  workspaceSlug: string,
  noteRef: string
): Promise<CoordinationNoteReadResponse> {
  const slug = workspaceSlug.trim();
  const ref = noteRef.trim();
  if (!slug || !ref) {
    throw new CoordinationNoteReadError('Workspace slug and note ref are required', 'NOT_CONFIGURED');
  }

  try {
    const { data } = await apiClient.get<CoordinationNoteReadResponse>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/coordination-note`,
      { params: { ref } }
    );
    return {
      ok: Boolean(data?.ok ?? data?.bodyMarkdown),
      ref: data?.ref || ref,
      stem: data?.stem,
      bodyMarkdown: data?.bodyMarkdown,
      unavailable: data?.unavailable,
      message: data?.message,
    };
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    const status = axiosErr.response?.status;
    if (status === 404) {
      return {
        ok: false,
        ref,
        unavailable: true,
        message:
          'Coordination note HTTP read is not available yet (G3). Note path is shown for operators; respond stays deferred.',
      };
    }
    const detail = axiosErr.response?.data?.detail;
    throw new CoordinationNoteReadError(
      typeof detail === 'string' ? detail : axiosErr.message || 'Failed to load coordination note',
      'REQUEST_FAILED',
      status
    );
  }
}
